import { useState } from 'react';
import { Icon } from '@/deploy/components/ui/Icon';
import { Badge } from '@/deploy/components/ui/Badge';
import { Button } from '@/deploy/components/ui/Button';
import { Modal } from '@/deploy/components/ui/Modal';
import { GroupBadge } from '@/deploy/components/ui/GroupBadge';
import { apiServicesService, type ApiService } from '@/deploy/services/api-services.service';
import type { ApiEnvId } from '@/deploy/services/api-pterodactyl-config.service';
import type { ProjectGroup } from '@/deploy/types';
import { ENVIRONMENT_BY_ID } from '@/deploy/types';
import { API_ENV_GROUPS, apiGroupLabel, canonicalApiEnv } from '@/deploy/utils/apiEnvs';
import {
  DEFAULT_INSTANCE, MAX_API_INSTANCES, NONE_INSTANCE, NONE_INSTANCE_LABEL, apiInstances, citationsText, endpointOn,
  findInstance, hasServer, instanceCitations, instanceErrorMessage, instanceLabel, isDefaultInstance, isNoneInstance,
  linkOn, placeholderSlug, qualifiedPlaceholders, routedInstance, routingTargetLabel, servedGroupsOf, versionOn,
} from '@/deploy/utils/apiInstances';
import { ApiInstanceModal } from './ApiInstanceModal';
import { ApiInstanceDeleteModal } from './ApiInstanceDeleteModal';
import pageStyles from '@/deploy/pages/ApiServices.module.scss';
import styles from './ApiInstances.module.scss';

// ============================================================
// Répartition d'une API sur un env : pour chaque groupe de jeu, l'instance qui le sert, ou
// « Aucune » (le groupe ne reçoit pas l'API). Enregistrer envoie la map de tous les groupes
// ({} si tous sont sur la Principale).
// ============================================================

/** Valeur spéciale du select : ouvre la création d'une instance. */
export const NEW_INSTANCE = '__new__';

export const ROUTING_HELP = "Au démarrage, chaque serveur de jeu reçoit l'instance de son groupe (variable RECUBE_GROUP). "
  + 'Un groupe non réparti, et un serveur sans RECUBE_GROUP, reçoivent la Principale. '
  + `Un groupe réglé sur « ${NONE_INSTANCE_LABEL} » ne reçoit pas l'API : ses placeholders restent non remplacés, `
  + "y compris les placeholders qualifiés [[X_HOST@GROUPE]] d'un serveur qui sert plusieurs groupes (le proxy). "
  + "Pris en compte au prochain démarrage de chaque serveur, y compris après un crash.";

/** « [[MODERATIONAPI_HOST]] et [[MODERATIONAPI_PORT]] » : placeholders de l'API dans les configs. */
function placeholdersOf(api: Pick<ApiService, 'name'>): string {
  const slug = placeholderSlug(api.name);
  return `[[${slug}_HOST]] et [[${slug}_PORT]]`;
}

/**
 * Serveur qui sert plusieurs groupes (le proxy) : il lit l'endpoint public avec ?group=<clé> pour
 * chaque qualificatif @GROUPE, donc « Aucune » laisse aussi ses placeholders qualifiés bruts.
 */
const PROXY_SCOPE = 'sur un serveur qui sert plusieurs groupes (le proxy)';

/**
 * Ce que recevra le groupe si la répartition lui donne cette instance : ip:port et version,
 * ou pourquoi il ne recevra rien (« Aucune », pas de serveur, lien cassé, instance disparue).
 */
export function RoutingTargetState({ api, instKey, envId, group, onCreateServer }: {
  api: ApiService;
  instKey: string;
  envId: ApiEnvId;
  group: ProjectGroup;
  onCreateServer?: () => void;
}) {
  const env = canonicalApiEnv(envId);
  const envLabel = apiGroupLabel(env);
  if (isNoneInstance(instKey)) {
    return (
      <span className={styles.instNoteWarn}>
        <Icon name="warn" size={10} /> Les serveurs {group.label} de {envLabel} ne recevront pas {api.name} :
        {' '}ses placeholders <span className={styles.mono}>{placeholdersOf(api)}</span> resteront non remplacés,
        {' '}comme <span className={styles.mono}>{qualifiedPlaceholders(api, [group.key])}</span> {PROXY_SCOPE}
      </span>
    );
  }
  const inst = findInstance(api, instKey);
  if (!inst) {
    return <span className={styles.instNoteDanger}>Instance « {instKey} » introuvable : recharge la page</span>;
  }
  const link = linkOn(inst, env);
  const endpoint = endpointOn(inst, env);
  const version = versionOn(inst, env);
  if (hasServer(inst, env)) {
    return <span className={styles.mono}>{endpoint ?? 'adresse inconnue'}{version ? ` · v${version}` : ''}</span>;
  }
  if (link === 'ambiguous') {
    return (
      <span className={styles.instNoteWarn}>
        <Icon name="warn" size={10} /> Plusieurs serveurs : rattache le bon{endpoint ? ` (servi pour l'instant : ${endpoint})` : ''}
      </span>
    );
  }
  if (link === 'missing' || link === 'broken') {
    return (
      <span className={styles.instNoteDanger}>
        <Icon name="x" size={10} /> {link === 'missing' ? 'Serveur introuvable' : 'Lien cassé'} sur {envLabel} :
        {' '}{api.name} non servie aux serveurs {group.label}
      </span>
    );
  }
  return (
    <>
      <span className={styles.instNoteWarn}>
        <Icon name="warn" size={10} /> Pas de serveur sur {envLabel} : les serveurs {group.label} de {envLabel}
        {' '}ne recevront pas {api.name}
      </span>
      {onCreateServer && (
        <Button variant="softPrimary" size="sm" leftIcon={<Icon name="plus" size={11} />} onClick={onCreateServer}>
          Créer le serveur…
        </Button>
      )}
    </>
  );
}

/** Avertissement de confirmation si le groupe ne recevra pas l'API, sinon null. */
export function routingWarning(api: ApiService, instKey: string, envId: ApiEnvId, group: ProjectGroup): string | null {
  const env = canonicalApiEnv(envId);
  const envLabel = apiGroupLabel(env);
  if (isNoneInstance(instKey)) {
    return `Les serveurs du groupe ${group.label} ne recevront pas ${api.name} sur ${envLabel} (« ${NONE_INSTANCE_LABEL} ») :`
      + ` ses placeholders ${placeholdersOf(api)} resteront non remplacés, comme ${qualifiedPlaceholders(api, [group.key])}`
      + ` ${PROXY_SCOPE}`;
  }
  const inst = findInstance(api, instKey);
  if (!inst) return `L'instance « ${instKey} » n'existe plus : les serveurs ${group.label} ne recevront plus ${api.name}`;
  if (hasServer(inst, env)) return null;
  const link = linkOn(inst, env);
  if (link === 'ambiguous') return `${instanceLabel(inst)} a plusieurs serveurs sur ${envLabel} : le premier trouvé est servi`;
  if (link === 'missing' || link === 'broken') {
    return `${instanceLabel(inst)} a un ${link === 'missing' ? 'serveur introuvable' : 'lien cassé'} sur ${envLabel} : les serveurs ${group.label} ne recevront plus ${api.name}`;
  }
  return `${instanceLabel(inst)} n'a pas de serveur sur ${envLabel} : les serveurs ${group.label} ne recevront plus ${api.name}`;
}

/** Corps de PUT routing : tous les groupes, ou {} si tous sont sur la Principale. */
export function routingBody(groups: ProjectGroup[], valueOf: (g: ProjectGroup) => string): Record<string, string> {
  const map: Record<string, string> = {};
  for (const g of groups) map[g.key] = valueOf(g);
  return Object.values(map).every((v) => v === DEFAULT_INSTANCE) ? {} : map;
}

/** Libellé d'un changement pour la confirmation : « Iris → Iris (172.27.2.20:25631) », « Lobby → Aucune ». */
export function routingChangeText(api: ApiService, envId: ApiEnvId, group: ProjectGroup, instKey: string): string {
  const inst = findInstance(api, instKey);
  const ep = inst ? endpointOn(inst, canonicalApiEnv(envId)) : undefined;
  return `${group.label} → ${routingTargetLabel(api, instKey)}${ep ? ` (${ep})` : ''}`;
}

/** Option « (introuvable) » du select : valeur enregistrée qui n'est ni une instance ni « Aucune ». */
export function isUnknownTarget(api: ApiService, value: string): boolean {
  return !isNoneInstance(value) && !findInstance(api, value);
}

/** Options du select d'instance, communes à la modale et à la matrice : instances, « Aucune », création. */
export function InstanceOptions({ api }: { api: ApiService }) {
  const all = apiInstances(api);
  const full = all.length >= MAX_API_INSTANCES;
  return (<>
    {all.map((i) => <option key={i.key} value={i.key}>{instanceLabel(i)}</option>)}
    <option value={NONE_INSTANCE}>{NONE_INSTANCE_LABEL} (le groupe ne reçoit pas l'API)</option>
    <option value={NEW_INSTANCE} disabled={full}>
      {full ? `+ Nouvelle instance… (${MAX_API_INSTANCES} instances maximum par API)` : '+ Nouvelle instance…'}
    </option>
  </>);
}

type Child =
  | { kind: 'create'; group?: ProjectGroup }
  | { kind: 'server'; key: string }
  | { kind: 'rename'; key: string }
  | { kind: 'delete'; key: string };

export function ApiRoutingModal({ projectId, api, envId, groups, onClose, onChanged }: {
  projectId: string;
  api: ApiService;
  envId: ApiEnvId;
  groups: ProjectGroup[];
  onClose: () => void;
  onChanged: () => Promise<void> | void;
}) {
  const env = canonicalApiEnv(envId);
  const envLabel = apiGroupLabel(env);
  const [choice, setChoice] = useState<Record<string, string>>(
    () => Object.fromEntries(groups.map((g) => [g.key, routedInstance(api, env, g.key)])));
  const [child, setChild] = useState<Child | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const all = apiInstances(api);
  const valueOf = (g: ProjectGroup) => choice[g.key] ?? routedInstance(api, env, g.key);
  const changed = groups.filter((g) => valueOf(g) !== routedInstance(api, env, g.key));

  const pick = (g: ProjectGroup, value: string) => {
    if (value === NEW_INSTANCE) { setChild({ kind: 'create', group: g }); return; }
    setChoice((c) => ({ ...c, [g.key]: value }));
  };

  const save = async () => {
    const warnings = groups.map((g) => routingWarning(api, valueOf(g), env, g)).filter((w): w is string => !!w);
    if (ENVIRONMENT_BY_ID[env].requiresConfirmation) {
      const text = `Répartition de ${api.name} sur ${envLabel} : ${changed.map((g) => routingChangeText(api, env, g, valueOf(g))).join(', ')}. `
        + 'Pris en compte au prochain démarrage de chaque serveur de jeu, y compris après un crash.'
        + (warnings.length > 0 ? `\n\n${warnings.join('\n')}` : '')
        + '\n\nContinuer ?';
      if (!confirm(text)) return;
    }
    setError(null);
    setSaving(true);
    try {
      await apiServicesService.setRouting(projectId, api.id, env, routingBody(groups, valueOf));
      await onChanged();
      onClose();
    } catch (e) {
      setError(instanceErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  // Échap ferme d'abord la modale enfant (les deux écoutent la touche)
  const close = () => { if (!child) onClose(); };

  return (
    <>
      <Modal
        open onClose={close}
        title={`Répartition de ${api.name}`}
        subtitle={envLabel}
        width="md"
        footer={<>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button variant="primary" onClick={() => void save()} loading={saving}
            disabled={saving || changed.length === 0 || groups.length === 0}>
            Enregistrer
          </Button>
        </>}
      >
        <div className={pageStyles.form}>
          <div className={styles.helpBox}>{ROUTING_HELP}</div>
          {error && <div className={pageStyles.error}>{error}</div>}
          {groups.length === 0 && (
            <div className={pageStyles.hintBox}>
              <Icon name="warn" size={12} /> Aucun groupe de jeu dans ce projet (Paramètres, Groupes de jeu) :
              tous les serveurs reçoivent la Principale.
            </div>
          )}

          {groups.length > 0 && (
            <div className={styles.routeList}>
              {groups.map((g) => {
                const value = valueOf(g);
                const isChanged = value !== routedInstance(api, env, g.key);
                return (
                  <div key={g.key} className={[styles.routeRow, isChanged && styles.routeRowChanged].filter(Boolean).join(' ')}>
                    <GroupBadge group={g} size="md" />
                    <select className={styles.select} value={value} onChange={(e) => pick(g, e.target.value)}
                      aria-label={`Instance servie au groupe ${g.label}`} disabled={saving}>
                      {isUnknownTarget(api, value) && <option value={value}>{value} (introuvable)</option>}
                      <InstanceOptions api={api} />
                    </select>
                    <div className={styles.routeState}>
                      <RoutingTargetState api={api} instKey={value} envId={env} group={g}
                        onCreateServer={findInstance(api, value) ? () => setChild({ kind: 'server', key: value }) : undefined} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <details className={styles.instSection} open={all.length > 1}>
            <summary>Instances de {api.name} ({all.length})</summary>
            <div className={styles.instCards}>
              <div className={styles.instCardActions}>
                <Button variant="secondary" size="sm" leftIcon={<Icon name="plus" size={11} />}
                  disabled={all.length >= MAX_API_INSTANCES}
                  title={all.length >= MAX_API_INSTANCES ? `${MAX_API_INSTANCES} instances maximum par API` : undefined}
                  onClick={() => setChild({ kind: 'create' })}>
                  Nouvelle instance…
                </Button>
              </div>
              {all.map((inst) => {
                const cited = instanceCitations(api, inst.key);
                const principal = isDefaultInstance(inst);
                // Groupes réglés sur « Aucune » (répartition enregistrée) : la Principale ne les reçoit pas
                const withoutInstance = principal ? instanceCitations(api, NONE_INSTANCE) : [];
                return (
                  <div key={inst.key} className={styles.instCard}>
                    <div className={styles.instCardHead}>
                      <strong>{instanceLabel(inst)}</strong>
                      <code className={styles.mono}>{inst.key}</code>
                      {principal && <Badge tone="muted">reçoit les groupes non répartis</Badge>}
                      <span className={styles.instSpacer} />
                      <div className={styles.instCardActions}>
                        {!hasServer(inst, env) && linkOn(inst, env) !== 'ambiguous' && (
                          <Button variant="softPrimary" size="sm" leftIcon={<Icon name="plus" size={11} />}
                            onClick={() => setChild({ kind: 'server', key: inst.key })}>
                            Créer le serveur sur {envLabel}
                          </Button>
                        )}
                        {!principal && (<>
                          <Button variant="ghost" size="sm" leftIcon={<Icon name="pencil" size={11} />}
                            onClick={() => setChild({ kind: 'rename', key: inst.key })}>
                            Renommer…
                          </Button>
                          <span title={cited.length > 0 ? `Sert ${citationsText(cited, groups)}` : undefined}>
                            <Button variant="softDanger" size="sm" leftIcon={<Icon name="x" size={11} />}
                              disabled={cited.length > 0} onClick={() => setChild({ kind: 'delete', key: inst.key })}>
                              Supprimer…
                            </Button>
                          </span>
                        </>)}
                      </div>
                    </div>
                    {!principal && cited.length > 0 && (
                      <span className={styles.instNote}>Sert {citationsText(cited, groups)} : non supprimable</span>
                    )}
                    {withoutInstance.length > 0 && (
                      <span className={styles.instNote}>
                        Sauf {citationsText(withoutInstance, groups)} (« {NONE_INSTANCE_LABEL} ») : ces serveurs ne
                        {' '}reçoivent aucune instance de {api.name}
                      </span>
                    )}
                    <div className={styles.instCardEnvs}>
                      {API_ENV_GROUPS.map((e) => {
                        const served = servedGroupsOf(api, inst, e, groups);
                        const link = linkOn(inst, e);
                        const ep = endpointOn(inst, e);
                        const v = versionOn(inst, e);
                        return (
                          <div key={e} className={styles.instCardEnv}>
                            <strong>{apiGroupLabel(e)}</strong>
                            <span className={styles.mono}>
                              {hasServer(inst, e) ? `${ep ?? 'serveur'}${v ? ` · v${v}` : ''}`
                                : link === 'ambiguous' ? 'plusieurs serveurs'
                                  : link === 'missing' ? 'serveur introuvable'
                                    : link === 'broken' ? 'lien cassé'
                                      : link === undefined ? 'état inconnu' : 'pas de serveur'}
                            </span>
                            {groups.length > 0 && (
                              <span>
                                {served.length > 0
                                  ? served.map((g) => <GroupBadge key={g.key} group={g} />)
                                  : 'aucun groupe'}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </details>
        </div>
      </Modal>

      {child?.kind === 'create' && (
        <ApiInstanceModal
          projectId={projectId} api={api} envId={env} mode="create" initialLabel={child.group?.label}
          onClose={() => setChild(null)} onChanged={onChanged}
          onCreated={(key) => { if (child.group) setChoice((c) => ({ ...c, [child.group!.key]: key })); }}
        />
      )}
      {(child?.kind === 'server' || child?.kind === 'rename') && (
        <ApiInstanceModal
          projectId={projectId} api={api} envId={env} mode={child.kind} instanceKey={child.key}
          onClose={() => setChild(null)} onChanged={onChanged}
        />
      )}
      {child?.kind === 'delete' && (
        <ApiInstanceDeleteModal
          projectId={projectId} api={api} instanceKey={child.key} groups={groups}
          onClose={() => setChild(null)} onChanged={onChanged}
        />
      )}
    </>
  );
}
